import React, { useCallback, useEffect, useRef, useState } from "react";
import { Tooltip } from "@material-ui/core";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import DoubleArrowIcon from "@material-ui/icons/DoubleArrow";
import { SunIcon, MoonIcon } from "../DaytimeIcon";
import { Day, DragState, TimeRange, WeeklyCalendarProps } from "./types";
import {
  DAYS,
  DEFAULT_END_TIME,
  DEFAULT_SCROLL_SLOT,
  DEFAULT_START_TIME,
  SLOT_HEIGHT,
  TIME_SLOTS,
} from "./constants";
import {
  canCopyAllEventsToDay,
  clampRangeToWindow,
  computePreviewEvent,
  formatOccupiedBlockLabel,
  getDayEnabledRange,
  getOccupiedRangesForDay,
  isDayDisabled,
  isEventVisible,
  slotIndexToTime,
  timeToSlotIndex,
} from "./utils";
import { useScheduleActions } from "./useScheduleActions";
import { useDeleteEventConfirmation } from "./useDeleteEventConfirmation";
import DeleteEventConfirmDialog from "./DeleteEventConfirmDialog";
import {
  BlockedSlotOverlay,
  BodyArea,
  CalendarLayout,
  Container,
  CopyDayButton,
  DayColumn,
  DayHeaderCell,
  DeleteButton,
  EventBlock,
  EventLabel,
  HeaderBar,
  OccupiedEventBlock,
  OccupiedLabel,
  ResizeHandle,
  ScrollArea,
  TimeColumn,
  TimeHeader,
  TimeLabel,
} from "./WeeklyCalendarDesktop.styles";

const WeeklyCalendarDesktop: React.FC<WeeklyCalendarProps> = ({
  weekdaysEnabled = false,
  weekendEnabled = false,
  showSun = false,
  showMoon = false,
  showCopyPreviousDay = false,
  confirmDeleteEvent = false,
  startTime = DEFAULT_START_TIME,
  endTime = DEFAULT_END_TIME,
  value,
  onChange,
  occupiedBlocks,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const dayColumnRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const [dragState, setDragState] = useState<DragState | null>(null);
  const dragRef = useRef<DragState | null>(null);
  const valueRef = useRef(value);
  valueRef.current = value;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const weekdaysEnabledRef = useRef(weekdaysEnabled);
  weekdaysEnabledRef.current = weekdaysEnabled;
  const weekendEnabledRef = useRef(weekendEnabled);
  weekendEnabledRef.current = weekendEnabled;

  // La rejilla solo muestra la ventana [startTime, endTime]; todos los slots
  // siguen siendo absolutos y las posiciones en pantalla se desplazan windowMinSlot.
  const windowMinSlot = timeToSlotIndex(startTime);
  const windowMaxSlot = timeToSlotIndex(endTime);
  // Dos carriles por día (preferencia | ocupado) solo si hay bloques ocupados.
  const splitLanes = (occupiedBlocks?.length ?? 0) > 0;

  const getDayWindowRange = (day: Day) =>
    clampRangeToWindow(
      getDayEnabledRange(day, weekdaysEnabled, weekendEnabled),
      startTime,
      endTime
    );

  const { copyFromPreviousDay, deleteEvent } = useScheduleActions(
    value,
    onChange,
    weekdaysEnabled,
    weekendEnabled,
    startTime,
    endTime
  );

  const { requestDelete, pending, onConfirm, onCancel } =
    useDeleteEventConfirmation(deleteEvent, confirmDeleteEvent);

  const updateDrag = (state: DragState | null) => {
    dragRef.current = state;
    setDragState(state);
  };

  const getSlotFromMouseY = useCallback((e: MouseEvent | React.MouseEvent, dayKey: string): number => {
    const col = dayColumnRefs.current[dayKey];
    if (!col) return windowMinSlot;
    const rect = col.getBoundingClientRect();
    const y = e.clientY - rect.top;
    return Math.max(
      windowMinSlot,
      Math.min(windowMaxSlot - 1, windowMinSlot + Math.floor(y / SLOT_HEIGHT))
    );
  }, [windowMinSlot, windowMaxSlot]);

  useEffect(() => {
    const handleMove = (e: MouseEvent) => {
      const drag = dragRef.current;
      if (!drag) return;
      const slotIndex = getSlotFromMouseY(e, drag.dayKey);
      if (slotIndex !== drag.currentSlotIndex) {
        updateDrag({ ...drag, currentSlotIndex: slotIndex });
      }
    };

    const handleUp = () => {
      const drag = dragRef.current;
      if (!drag) return;
      const day = DAYS.find(d => d.key === drag.dayKey)!;
      const range = clampRangeToWindow(
        getDayEnabledRange(day, weekdaysEnabledRef.current, weekendEnabledRef.current),
        startTime,
        endTime
      );
      const dayEvents = valueRef.current[drag.dayKey] || [];
      const preview = computePreviewEvent(drag, dayEvents, range);
      const newSchedule = { ...valueRef.current };
      const dayRanges = [...dayEvents];
      const newEvent: TimeRange = [slotIndexToTime(preview.start), slotIndexToTime(preview.end)];

      if (drag.mode === "create") {
        dayRanges.push(newEvent);
      } else {
        dayRanges[drag.eventIndex] = newEvent;
      }

      newSchedule[drag.dayKey] = dayRanges;
      onChangeRef.current(newSchedule);
      updateDrag(null);
    };

    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
    };
  }, [getSlotFromMouseY, startTime, endTime]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = Math.max(
        0,
        (DEFAULT_SCROLL_SLOT - windowMinSlot) * SLOT_HEIGHT
      );
    }
  }, [windowMinSlot]);

  const handleColumnMouseDown = (e: React.MouseEvent, day: Day) => {
    const range = getDayWindowRange(day);
    if (!range) return;
    if ((e.target as HTMLElement).closest("[data-event]")) return;
    e.preventDefault();
    const slotIndex = getSlotFromMouseY(e, day.key);
    if (slotIndex < range.minSlot || slotIndex >= range.maxSlot) return;
    updateDrag({
      mode: "create",
      dayKey: day.key,
      eventIndex: -1,
      initialSlotIndex: slotIndex,
      currentSlotIndex: slotIndex,
      originalStart: 0,
      originalEnd: 0,
    });
  };

  const handleEventMouseDown = (
    e: React.MouseEvent,
    day: Day,
    idx: number,
    startIdx: number,
    endIdx: number
  ) => {
    e.stopPropagation();
    e.preventDefault();
    if (!getDayWindowRange(day)) return;
    const slotIndex = getSlotFromMouseY(e, day.key);
    updateDrag({
      mode: "move",
      dayKey: day.key,
      eventIndex: idx,
      initialSlotIndex: slotIndex,
      currentSlotIndex: slotIndex,
      originalStart: startIdx,
      originalEnd: endIdx,
    });
  };

  const handleResizeMouseDown = (
    e: React.MouseEvent,
    day: Day,
    idx: number,
    startIdx: number,
    endIdx: number,
    mode: "resize-top" | "resize-bottom"
  ) => {
    e.stopPropagation();
    e.preventDefault();
    if (!getDayWindowRange(day)) return;
    updateDrag({
      mode,
      dayKey: day.key,
      eventIndex: idx,
      initialSlotIndex: mode === "resize-top" ? startIdx : endIdx,
      currentSlotIndex: mode === "resize-top" ? startIdx : endIdx,
      originalStart: startIdx,
      originalEnd: endIdx,
    });
  };

  return (
    <>
    <Container $dragging={!!dragState}>
      <ScrollArea ref={scrollRef}>
        <CalendarLayout>
          <HeaderBar>
            <TimeHeader />
            {DAYS.map((day, dayIndex) => {
              const disabled = isDayDisabled(day, weekdaysEnabled, weekendEnabled);
              const currentEvents = value[day.key] || [];
              const isFirstDay = dayIndex === 0;
              const prevDay = !isFirstDay ? DAYS[dayIndex - 1] : null;
              const prevDayDisabled = prevDay ? isDayDisabled(prevDay, weekdaysEnabled, weekendEnabled) : true;
              const prevDayEvents = prevDay
                ? (value[prevDay.key] || []).filter((event) =>
                    isEventVisible(event, startTime, endTime)
                  )
                : [];
              const currentRange = getDayWindowRange(day);
              const canCopy = !isFirstDay && !disabled && prevDayEvents.length > 0 && !prevDayDisabled && currentRange !== null && canCopyAllEventsToDay(prevDayEvents, currentRange, currentEvents);
              return (
                <DayHeaderCell key={day.key} $disabled={disabled}>
                  {day.fullLabel}
                  {showCopyPreviousDay && (
                    <Tooltip title="Copiar del día anterior" arrow placement="top">
                      <CopyDayButton
                        $disabled={!canCopy}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (canCopy) copyFromPreviousDay(dayIndex);
                        }}
                      >
                        <DoubleArrowIcon style={{ fontSize: 12 }} />
                        <FileCopyIcon style={{ fontSize: 12 }} />
                        <span style={{ fontSize: "0.65rem", margin: "0 2px" }}>Copiar</span>
                      </CopyDayButton>
                    </Tooltip>
                  )}
                </DayHeaderCell>
              );
            })}
          </HeaderBar>
          <BodyArea>
            <TimeColumn>
              {TIME_SLOTS.slice(windowMinSlot, windowMaxSlot).map((slot, i) => {
                const slotIndex = windowMinSlot + i;
                const isDaytime = slotIndex >= 18 && slotIndex < 42;
                return (
                  <TimeLabel key={slot} $isHour={slot.endsWith(":00")}>
                    {isDaytime ? (showSun && <SunIcon />) : (showMoon && <MoonIcon />)}
                    {slot}
                  </TimeLabel>
                );
              })}
            </TimeColumn>
            {DAYS.map((day) => {
              const range = getDayWindowRange(day);
              const disabled = !range;
              const events = value[day.key] || [];
              return (
                <DayColumn
                  key={day.key}
                  ref={(el) => { dayColumnRefs.current[day.key] = el; }}
                  $disabled={disabled}
                  $slots={windowMaxSlot - windowMinSlot}
                  onMouseDown={(e) => handleColumnMouseDown(e, day)}
                >
                  {range && range.minSlot > windowMinSlot && (
                    <BlockedSlotOverlay style={{ top: 0, height: (range.minSlot - windowMinSlot) * SLOT_HEIGHT }} />
                  )}
                  {range && range.maxSlot < windowMaxSlot && (
                    <BlockedSlotOverlay style={{ top: (range.maxSlot - windowMinSlot) * SLOT_HEIGHT, bottom: 0 }} />
                  )}
                  {getOccupiedRangesForDay(occupiedBlocks, day.key).map(
                    ({ block, range: occupied }, idx) => {
                      // Solo lectura: sin ✕, sin redimensionado y sin arrastre de creación encima.
                      const s = Math.max(timeToSlotIndex(occupied[0]), windowMinSlot);
                      const eIdx = Math.min(timeToSlotIndex(occupied[1]), windowMaxSlot);
                      if (s >= eIdx) return null;
                      const label = formatOccupiedBlockLabel(block);
                      return (
                        <OccupiedEventBlock
                          key={`occupied-${block.id}-${idx}`}
                          $kind={block.kind}
                          data-kind={block.kind}
                          data-occupied="true"
                          title={`${label} ${occupied[0]}-${occupied[1]}`}
                          style={{
                            top: (s - windowMinSlot) * SLOT_HEIGHT,
                            height: (eIdx - s) * SLOT_HEIGHT,
                          }}
                          onMouseDown={(ev) => {
                            ev.stopPropagation();
                            ev.preventDefault();
                          }}
                        >
                          <OccupiedLabel $kind={block.kind}>{label}</OccupiedLabel>
                        </OccupiedEventBlock>
                      );
                    }
                  )}
                  {!disabled &&
                    events.map((event, idx) => {
                      if (!isEventVisible(event, startTime, endTime)) return null;
                      const s = timeToSlotIndex(event[0]);
                      const eIdx = timeToSlotIndex(event[1]);
                      if (dragState?.dayKey === day.key && dragState.eventIndex === idx)
                        return null;
                      return (
                        <EventBlock
                          key={idx}
                          $split={splitLanes}
                          data-event="true"
                          style={{
                            top: (s - windowMinSlot) * SLOT_HEIGHT,
                            height: (eIdx - s) * SLOT_HEIGHT,
                          }}
                          onMouseDown={(ev) =>
                            handleEventMouseDown(ev, day, idx, s, eIdx)
                          }
                          onDoubleClick={() =>
                            requestDelete(day, idx, event)
                          }
                        >
                          <EventLabel>{event[0]}-{event[1]}</EventLabel>
                          <DeleteButton
                            data-delete
                            onMouseDown={(ev) => {
                              ev.stopPropagation();
                              ev.preventDefault();
                              requestDelete(day, idx, event);
                            }}
                          >
                            ✕
                          </DeleteButton>
                          <ResizeHandle
                            $position="top"
                            onMouseDown={(ev) =>
                              handleResizeMouseDown(ev, day, idx, s, eIdx, "resize-top")
                            }
                          />
                          <ResizeHandle
                            $position="bottom"
                            onMouseDown={(ev) =>
                              handleResizeMouseDown(ev, day, idx, s, eIdx, "resize-bottom")
                            }
                          />
                        </EventBlock>
                      );
                    })}
                  {dragState?.dayKey === day.key &&
                    (() => {
                      const preview = computePreviewEvent(dragState, events, range);
                      return (
                        <EventBlock
                          $preview
                          $split={splitLanes}
                          data-event="true"
                          style={{
                            top: (preview.start - windowMinSlot) * SLOT_HEIGHT,
                            height: (preview.end - preview.start) * SLOT_HEIGHT,
                          }}
                        >
                          <EventLabel>
                            {slotIndexToTime(preview.start)}-{slotIndexToTime(preview.end)}
                          </EventLabel>
                        </EventBlock>
                      );
                    })()}
                </DayColumn>
              );
            })}
          </BodyArea>
        </CalendarLayout>
      </ScrollArea>
    </Container>
    {pending && (
      <DeleteEventConfirmDialog
        day={pending.day}
        event={pending.event}
        onConfirm={onConfirm}
        onCancel={onCancel}
      />
    )}
    </>
  );
};

export default WeeklyCalendarDesktop;
